import { useTranslation } from 'react-i18next'

import { BannerApp } from '@widgets/app-banner'

import SEO from '@shared/components/SEO'
import { BRAND_NAME } from '@shared/contants/app'
import { URL_MAIN_PAGE } from '@shared/contants'
import Breadcrumbs from '@shared/ui/breadcrumbs'
import Button from '@shared/ui/button'
import Container from '@shared/ui/container'
import Wrapper from '@shared/ui/wrapper'

import styles from './styles.module.scss'

const statKeys = [ 'first', 'second', 'third', 'fourth' ] as const
const solutionKeys = [ 'platform', 'loyalty', 'experience', 'analytics' ] as const
const valueKeys = [ 'speed', 'law', 'automation', 'partnership' ] as const
const stackKeys = [ 'clientApp', 'pickerApp', 'supervisorApp', 'courierApp', 'admin', 'callCenter', 'bi' ] as const
const stepKeys = [ 'discovery', 'processes', 'services', 'integrations', 'training', 'pilot', 'rollout', 'support' ] as const
const modelKeys = [ 'saas', 'onPremise', 'licenseSupport' ] as const

export default function AboutPage () {
  const { t: ta } = useTranslation('about')
  const { t: ts } = useTranslation('seo')

  return (
    <Container>
      <SEO
        title={ts('about.title', { brand: BRAND_NAME })}
        description={ts('about.description', { brand: BRAND_NAME })}
      />
      <Wrapper className={styles.wrapper}>
        <div className={styles.header}>
          <Breadcrumbs
            className={styles.breadcrumbs}
            links={[
              {
                title: ta('page'),
              },
            ]}
          />
          <h1 className={styles.pageTitle}>{ta('content.title')}</h1>
        </div>

        <div className={styles.content}>
          <section className={styles.hero}>
            <div className={styles.hero__content}>
              <span className={styles.eyebrow}>{ta('content.hero.eyebrow')}</span>
              <h2 className={styles.hero__title}>{ta('content.hero.title')}</h2>
              <p className={styles.hero__description}>{ta('content.hero.description')}</p>

              <div className={styles.hero__actions}>
                <Button to="/contacts" size="large">
                  {ta('content.hero.primaryCta')}
                </Button>
                <Button
                  to={URL_MAIN_PAGE}
                  theme="white"
                  size="large"
                  className={styles.secondaryAction}
                >
                  {ta('content.hero.secondaryCta')}
                </Button>
              </div>
            </div>

            <div className={styles.hero__panel}>
              <div className={styles.hero__card}>
                <h3 className={styles.hero__cardTitle}>{ta('content.hero.panelTitle')}</h3>
                <p className={styles.hero__cardText}>{ta('content.hero.panelDescription')}</p>
              </div>

              <div className={styles.hero__mascot}>
                <img src="/img/tobyl-mascot/mascot-full.png" alt="Фирменный персонаж Тобыл" />
              </div>

              <div className={styles.statsGrid}>
                {statKeys.map((key) => (
                  <div key={key} className={styles.statCard}>
                    <div className={styles.statCard__value}>{ta(`content.stats.${key}.value`)}</div>
                    <div className={styles.statCard__label}>{ta(`content.stats.${key}.label`)}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionKicker}>{ta('content.solutions.kicker')}</span>
              <h2 className={styles.sectionTitle}>{ta('content.solutions.title')}</h2>
              <p className={styles.sectionDescription}>{ta('content.solutions.description')}</p>
            </div>

            <div className={styles.solutionsGrid}>
              {solutionKeys.map((key) => (
                <article key={key} className={styles.solutionCard}>
                  <div className={styles.solutionCard__index}>{ta(`content.solutions.${key}.index`)}</div>
                  <h3 className={styles.solutionCard__title}>{ta(`content.solutions.${key}.title`)}</h3>
                  <p className={styles.solutionCard__description}>{ta(`content.solutions.${key}.description`)}</p>
                </article>
              ))}
            </div>
          </section>

          <section className={styles.section}>
            <div className={styles.valueStrip}>
              <div className={styles.sectionHeader}>
                <span className={styles.sectionKicker}>{ta('content.values.kicker')}</span>
                <h2 className={styles.sectionTitle}>{ta('content.values.title')}</h2>
              </div>

              <div className={styles.valuesGrid}>
                {valueKeys.map((key) => (
                  <div key={key} className={styles.valueCard}>
                    <h3 className={styles.valueCard__title}>{ta(`content.values.${key}.title`)}</h3>
                    <p className={styles.valueCard__description}>{ta(`content.values.${key}.description`)}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className={styles.section}>
            <div className={styles.stackLayout}>
              <div className={styles.stackIntro}>
                <span className={styles.sectionKicker}>{ta('content.stack.kicker')}</span>
                <h2 className={styles.sectionTitle}>{ta('content.stack.title')}</h2>
                <p className={styles.sectionDescription}>{ta('content.stack.description')}</p>
              </div>

              <div className={styles.stackGrid}>
                {stackKeys.map((key) => (
                  <article key={key} className={styles.stackCard}>
                    <h3 className={styles.stackCard__title}>{ta(`content.stack.${key}.title`)}</h3>
                    <p className={styles.stackCard__description}>{ta(`content.stack.${key}.description`)}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionKicker}>{ta('content.workflow.kicker')}</span>
              <h2 className={styles.sectionTitle}>{ta('content.workflow.title')}</h2>
              <p className={styles.sectionDescription}>{ta('content.workflow.description')}</p>
            </div>

            <div className={styles.timeline}>
              {stepKeys.map((key, index) => (
                <article key={key} className={styles.timelineCard}>
                  <div className={styles.timelineCard__top}>
                    <span className={styles.timelineCard__number}>{String(index + 1).padStart(2, '0')}</span>
                    <span className={styles.timelineCard__duration}>{ta(`content.workflow.${key}.duration`)}</span>
                  </div>
                  <h3 className={styles.timelineCard__title}>{ta(`content.workflow.${key}.title`)}</h3>
                  <p className={styles.timelineCard__description}>{ta(`content.workflow.${key}.description`)}</p>
                </article>
              ))}
            </div>
          </section>

          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionKicker}>{ta('content.models.kicker')}</span>
              <h2 className={styles.sectionTitle}>{ta('content.models.title')}</h2>
              <p className={styles.sectionDescription}>{ta('content.models.description')}</p>
            </div>

            <div className={styles.modelsGrid}>
              {modelKeys.map((key) => (
                <article key={key} className={styles.modelCard}>
                  <h3 className={styles.modelCard__title}>{ta(`content.models.${key}.title`)}</h3>
                  <div className={styles.modelCard__price}>{ta(`content.models.${key}.price`)}</div>
                  <p className={styles.modelCard__description}>{ta(`content.models.${key}.description`)}</p>
                </article>
              ))}
            </div>
          </section>

          <section className={styles.section}>
            <div className={styles.cta}>
              <div className={styles.cta__content}>
                <span className={styles.sectionKicker}>{ta('content.cta.kicker')}</span>
                <h2 className={styles.cta__title}>{ta('content.cta.title')}</h2>
                <p className={styles.cta__description}>{ta('content.cta.description')}</p>
              </div>

              <div className={styles.cta__actions}>
                <Button to="/contacts" size="large">
                  {ta('content.cta.primaryCta')}
                </Button>
                <Button
                  to={URL_MAIN_PAGE}
                  theme="secondary"
                  size="large"
                >
                  {ta('content.cta.secondaryCta')}
                </Button>
              </div>
            </div>
          </section>

          <div className={styles.bannerBlock}>
            <BannerApp />
          </div>
        </div>
      </Wrapper>
    </Container>
  )
}
